import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { Page } from '#components/Page';
import { useCategories } from '#hooks/useCategories';
import { useLocale } from '#hooks/useLocale';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { cor, tela, texto } from './estilo';
import { BlocoDeTipo } from './medias/BlocoDeTipo';
import type { Janela } from './medias/JanelaSeletor';
import { JanelaSeletor } from './medias/JanelaSeletor';
import type { Escopo } from './motor/escopo';
import { ROTULO_ESCOPO } from './motor/escopo';
import { calcularMedias } from './motor/medias';
import { CHAVE_PREF, lerMapa } from './motor/tiposDeGasto';
import { useHistorico } from './motor/useHistorico';

export function Medias() {
  const { t } = useTranslation();
  const locale = useLocale();
  /**
   * ⚠️⚠️ Validação no navegador (22/09/2026) — mesma cadeia que
   * `Inicio.tsx` já usa (`useResponsive()` aqui, `estreito` descendo por
   * `BlocoDeTipo` até `medias/LinhaDeMedia.tsx`). Sem isto, em 390px de
   * largura a linha de cada categoria (nome · média · este mês ·
   * diferença) passava da largura da tela e o valor da diferença era
   * cortado pelo `overflow-x: hidden` do body, sem scroll possível.
   */
  const { isNarrowWidth } = useResponsive();
  const [janela, setJanela] = useState<Janela>(6);
  // Mesmo escopo padrão da tela Mês. O seletor pessoal/empresa chega numa
  // tarefa futura; até lá, a tela mostra o pessoal, que é o caso dominante.
  const escopo: Escopo = 'pessoal';

  const mesAtual = monthUtils.currentMonth();
  const { linhas, meses, carregando, erro } = useHistorico(mesAtual, janela);

  /**
   * ⚠️⚠️ Revisão final da branch (Finding 4, Important) — esta tela FIXA
   * mês corrente + escopo pessoal, mas a tela Mês tem um cursor de mês e um
   * seletor pessoal/empresa: o dono pode chegar aqui vindo de um mês
   * diferente (via "ver detalhe", em `GastoMedioPorTipo`) ou de "empresa",
   * e a tela não diz qual mês/escopo está mostrando — um número sem rótulo
   * de período é o que vira desconfiança. Correção MÍNIMA pedida: só
   * imprimir o período no topo, não construir o seletor (isso fica pra uma
   * tarefa futura, quando as duas telas passarem a RECEBER mês/escopo em
   * vez de calcular os próprios).
   */
  const rotuloPeriodo = (
    <Text
      data-testid="medias-periodo"
      style={{ ...texto.corpo, color: cor.tintaFraca }}
    >
      <Text style={{ textTransform: 'capitalize' }}>
        {monthUtils.format(mesAtual, 'MMMM yyyy', locale)}
      </Text>
      {' · '}
      {t(ROTULO_ESCOPO[escopo])}
    </Text>
  );

  const { data: categorias } = useCategories();
  const nomes = new Map(categorias?.list.map(c => [c.id, c.name]) ?? []);

  const [prefBruta] = useSyncedPref(CHAVE_PREF);
  const mapa = lerMapa(prefBruta);

  /*
   * ⚠️ Fix round 1 (Important): erro e carregamento saem daqui por EARLY
   * RETURN, antes de qualquer `calcularMedias`. As duas chamadas de
   * `calcularMedias` varrem `linhas` inteiro e a segunda ainda ordena
   * `porCategoria` e monta os quatro baldes de `porTipo` — trabalho que
   * nesses dois estados é sempre descartado, porque nada disso é
   * renderizado. O seletor de janela continua visível e funcional nos dois
   * ramos: é justamente o que o dono vai querer trocar se a tela demorar.
   */
  if (erro) {
    return (
      <Page header={t('Médias')}>
        <View style={tela} data-testid="tela-medias">
          <JanelaSeletor janela={janela} onMudar={setJanela} />
          {rotuloPeriodo}
          <Text style={{ color: cor.saida }}>
            {t('Não consegui ler seu histórico: {{erro}}', {
              erro: erro.message,
            })}
          </Text>
        </View>
      </Page>
    );
  }

  if (carregando) {
    return (
      <Page header={t('Médias')}>
        <View style={tela} data-testid="tela-medias">
          <JanelaSeletor janela={janela} onMudar={setJanela} />
          {rotuloPeriodo}
          <Text>
            <Trans>Carregando…</Trans>
          </Text>
        </View>
      </Page>
    );
  }

  const medias = calcularMedias(linhas, meses, nomes, mapa, escopo);

  // O mês corrente, para a coluna "este mês" de cada linha.
  const doMesAtual = calcularMedias(linhas, [mesAtual], nomes, mapa, escopo);
  const esteMesPorCategoria = new Map(
    doMesAtual.porCategoria.map(c => [c.categoriaId, c.totalNaJanela]),
  );

  return (
    <Page header={t('Médias')}>
      <View style={tela} data-testid="tela-medias">
        <JanelaSeletor janela={janela} onMudar={setJanela} />
        {rotuloPeriodo}

        {/*
         * ⚠️⚠️ OBRIGATÓRIO: quantos meses entraram na conta de verdade.
         * Sem isto, uma janela de 6 meses com 1 mês de dado mostra uma
         * média seis vezes menor com cara de número certo — e o app foi
         * recriado em 07/09/2026, então ter pouco histórico é o caso
         * NORMAL aqui, não a exceção.
         */}
        <Text
          style={{ ...texto.corpo, color: cor.tintaFraca }}
          data-testid="medias-amostra"
        >
          {medias.mesesConsiderados === 1
            ? t('Média de 1 mês com movimento')
            : t('Média de {{n}} meses com movimento', {
                n: medias.mesesConsiderados,
              })}
        </Text>

        {medias.porTipo.map(({ tipo, media }) => (
          <BlocoDeTipo
            key={tipo}
            tipo={tipo}
            mediaDoTipo={media}
            categorias={medias.porCategoria.filter(c => c.tipo === tipo)}
            esteMesPorCategoria={esteMesPorCategoria}
            estreito={isNarrowWidth}
          />
        ))}
      </View>
    </Page>
  );
}
